import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const logout = useCallback(() => {
        localStorage.removeItem('nexusrig_token');
        localStorage.removeItem('nexusrig_user');
        setUser(null);
        setIsAuthenticated(false);
    }, []);

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('nexusrig_token');
            const savedUser = localStorage.getItem('nexusrig_user');
            
            if (token && savedUser) {
                try {
                    setUser(JSON.parse(savedUser));
                    setIsAuthenticated(true);
                    // Verify token is still valid
                    const response = await authAPI.getMe();
                    setUser(response.data);
                    localStorage.setItem('nexusrig_user', JSON.stringify(response.data));
                } catch (error) {
                    console.error('Auth verification failed:', error);
                    logout();
                }
            }
            setLoading(false);
        };

        initAuth();

        // Listen for logout events
        const handleLogout = () => logout();
        window.addEventListener('auth-logout', handleLogout);
        return () => window.removeEventListener('auth-logout', handleLogout);
    }, [logout]);

    const login = async (email, password, cfTurnstileResponse) => {
        const response = await authAPI.login({ email, password, cf_turnstile_response: cfTurnstileResponse || '' });
        const data = response.data;

        // Check if 2FA is required
        if (data.requires_2fa) {
            return { requires_2fa: true, temp_token: data.temp_token };
        }

        const { access_token, user: userData } = data;
        localStorage.setItem('nexusrig_token', access_token);
        localStorage.setItem('nexusrig_user', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
    };

    const verify2FA = async (tempToken, code) => {
        const response = await authAPI.verify2FA({ temp_token: tempToken, code });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('nexusrig_token', access_token);
        localStorage.setItem('nexusrig_user', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
    };

    const register = async (name, email, password, cfTurnstileResponse) => {
        const response = await authAPI.register({ name, email, password, cf_turnstile_response: cfTurnstileResponse || '' });
        const data = response.data;
        
        // New flow: requires email verification
        if (data.requires_verification) {
            return { requires_verification: true, message: data.message };
        }

        // Admin (first user) gets auto-login
        const { access_token, user: userData } = data;
        localStorage.setItem('nexusrig_token', access_token);
        localStorage.setItem('nexusrig_user', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
    };

    const isAdmin = user?.role === 'admin';

    return (
        <AuthContext.Provider value={{
            user,
            setUser,
            loading,
            isAuthenticated,
            setIsAuthenticated,
            isAdmin,
            login,
            verify2FA,
            register,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
